import { useEffect, useState, useMemo } from "react";
import { PostApiMethod } from "../../core/services/apiService";
import { fontWeight } from "html2canvas/dist/types/css/property-descriptors/font-weight";
import { PieBarChartParams } from "../../core/dto/dashboard.dto";

export function PieBarChart({
    apiEndpoint,
    startDate,
    endDate,
    chartName,
    payloadExtra = {},
    labelKey,
    valueKey,
    chartType = "bar",
    labelFormatter = (l) => l,
    valueFormatter = (v) => v,
    onChartLoaded
}: PieBarChartParams) {
    const [chartData, setChartData] = useState<any>();
    const [loaded, setLoaded] = useState(false);


    const secondsToHHMMSS = (secs: number) => {
        const h = Math.floor(secs / 3600)?.toString().padStart(2, "0");
        const m = Math.floor((secs % 3600) / 60)?.toString().padStart(2, "0");
        const s = Math.floor(secs % 60)?.toString().padStart(2, "0");
        return `${h}:${m}:${s}`;
    };


    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const payload = {
                    from_date: startDate ? startDate.toLocaleDateString("en-CA") : null,
                    to_date: endDate ? endDate.toLocaleDateString("en-CA") : null,
                    clientTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    ...payloadExtra
                };

                const response = await PostApiMethod(apiEndpoint, payload);
                if (!isMounted) return;
                if (response?.data?.data) {
                    const labels: string[] = [];
                    const data: number[] = [];

                    response.data?.data?.forEach((el: any) => {
                        labels.push(labelFormatter(el[labelKey]));
                        data.push(valueFormatter(el[valueKey]));
                    });

                    const count = data?.reduce((sum, val) => sum + val, 0);
                    const options: any =
                        chartType === "pie"
                            ? {
                                labels,
                                chart: {
                                    type: "pie",
                                    toolbar: {
                                        show: true,
                                        tools: {
                                            download: true,
                                        }
                                    }
                                },
                                legend: {
                                    show: true,
                                    position: "bottom",
                                    horizontalAlign: 'left',
                                    formatter: function (val: any, opts: any) {
                                        const count = opts.w.globals.series[opts.seriesIndex];
                                        const total = opts.w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                                        const percent = ((count / total) * 100).toFixed(1);

                                        if (chartName === "missed") {
                                            return `<div style="display: flex; align-items: center;">
                                                <span style="margin-right: 5px;">${val} - ${count} (${percent}%)</span>
                                                </div>`;
                                        } else {
                                            let hhmmssFormat = secondsToHHMMSS(count);
                                            return `<div style="display: flex; align-items: center;">
                                            <span style="margin-right: 5px;">${val} - ${hhmmssFormat} - (${percent}%)</span>
                                            </div>`;
                                        }
                                    },
                                },
                                colors: ["#439a43", "#0187C4", "#ff80aa", "#ffd480"],
                                dataLabels: {
                                    enabled: true,
                                    dropShadow: { enabled: false },
                                    style: { colors: ["#30360a"], fontSize: "16px", fontWeight: "normal" },
                                    formatter: function (val: number, opts: any) {
                                        const value = opts.w.globals.series[opts.seriesIndex];
                                        // const total = opts.w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                                        // const percent = ((value / total) * 100).toFixed(1); // Future use
                                        if (chartName !== "missed") {
                                            let hhmmssFormat = secondsToHHMMSS(value);
                                            return `${hhmmssFormat}`;
                                        } else {
                                            return `${value}`;
                                        }
                                    }
                                },
                                plotOptions: {
                                    pie: { dataLabels: { offset: -20 } }
                                },
                                tooltip: {
                                    enabled: true,
                                    y: {
                                        formatter: function (val: number, opts: any) {
                                            if (chartName !== "missed") {
                                                let hhmmssFormat = secondsToHHMMSS(val);
                                                return `${hhmmssFormat}`;
                                            } else {
                                                return `${val}`;
                                            }
                                        },
                                        title: {
                                            formatter: function (seriesName: string) {
                                                return seriesName + ":";
                                            }
                                        }
                                    }
                                }
                            } : {
                                chart: { id: "breakdown-chart" },
                                plotOptions: {
                                    bar: {
                                        distributed: true,
                                        dataLabels: {
                                            position: "top",
                                            style: { fontSize: '14px' }
                                        },
                                    }
                                },
                                legend: {
                                    show: true,
                                    position: "bottom",
                                    horizontalAlign: 'left',
                                    formatter: function (val: any, opts: any) {
                                        const agentName = val;
                                        const callsCount = opts.w.globals.series[0][opts.seriesIndex];
                                        const total = opts.w.globals.series[0].reduce((a: number, b: number) => a + b, 0);
                                        const percent = ((callsCount / total) * 100).toFixed(1);
                                        if (chartName === "AgentAvgCallLength") {
                                            let hhmmssFormat = secondsToHHMMSS(callsCount);
                                            return `<div style="display: flex; align-items: center;">
                                                    <span style="margin-right: 5px;">${agentName} - ${hhmmssFormat} (${percent}%)</span>
                                                    </div>`;
                                        } else {
                                            return `<div style="display: flex; align-items: center;">
                                                    <span style="margin-right: 5px;">${agentName} - ${callsCount} (${percent}%)</span>
                                                    </div>`;
                                        }
                                    },
                                },
                                colors: data.map((_, i) => ["#439a43", "#0187C4"][i % 2]),
                                xaxis: {
                                    categories: labels,
                                    labels: {
                                        show: true,
                                        style: { fontSize: '14px' }
                                    }
                                },
                                yaxis: {
                                    labels: {
                                        show: true,
                                        style: { fontSize: '14px' }
                                    }
                                },
                                dataLabels: {
                                    enabled: true,
                                    offsetY: -20,
                                    formatter: function (val: number, opts: any) {
                                        // const total = opts.w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                                        // const percent = ((val / total) * 100).toFixed(1); //Future use
                                        // return `${val}\n(${percent}%)`;
                                        if (chartName === "AgentAvgCallLength") {
                                            let hhmmssFormat = secondsToHHMMSS(val);
                                            return `${hhmmssFormat}`;
                                        } else {
                                            return `${val}`;
                                        }
                                    },
                                    style: {
                                        colors: ["#30360a"],
                                        fontSize: '13px',
                                        fontWeight: "normal"
                                    },
                                },
                                tooltip: {
                                    enabled: true,
                                    y: {
                                        formatter: function (val: number) {
                                            if (chartName === "AgentAvgCallLength") {
                                                let hhmmssFormat = secondsToHHMMSS(val);
                                                return `${hhmmssFormat}`;
                                            } else {
                                                return `${val}`;
                                            }
                                        },
                                        title: {
                                            formatter: function () {
                                                return chartName === "AgentAvgCallLength" ? "Duration" : "Total Calls";
                                            }
                                        }
                                    }
                                }
                            };

                    setChartData({ labels: options, series: chartType === "pie" ? data : [{ name: "", data }], count });
                    if (!loaded) {
                        onChartLoaded?.();
                        setLoaded(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching chart data:", error);
            }
        })();
        return () => { isMounted = false };
    }, [apiEndpoint, startDate, endDate, JSON.stringify(payloadExtra), labelKey, valueKey, chartType]);

    return useMemo(() => chartData, [chartData]);
}
